import React, { useMemo, useState } from 'react';
import { Card, Badge, Row, Col, Spinner, Form, Button } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { http } from '../../../features/jobs/api/http';

const fmt = n => Number(n || 0).toLocaleString();

async function fetchCompatible(params) {
  const { data } = await http.get('/api/redis/phantoms/compatible', { params });
  return data.phantoms || [];
}

// check if a phantom's wire specs are compatible with ANY of the product's conductors
function isWireCompatible(phantom, wireSpecs) {
  if (!wireSpecs || !wireSpecs.length) return true;

  const pType = String(phantom.wireType || '').toUpperCase();

  const pDiams = Array.isArray(phantom.wireDiameter)
    ? phantom.wireDiameter.map(Number).sort((a, b) => a - b)
    : phantom.wireDiameter != null
      ? [Number(phantom.wireDiameter)]
      : [];

  const pCounts = Array.isArray(phantom.numberOfWires)
    ? phantom.numberOfWires.map(Number).sort((a, b) => a - b)
    : phantom.numberOfWires != null
      ? [Number(phantom.numberOfWires)]
      : [];

  for (const spec of wireSpecs) {
    const sType = String(spec.wireType || '').toUpperCase();
    if (pType !== sType) continue;

    const sDiams = Array.isArray(spec.wireDiameter)
      ? spec.wireDiameter.map(Number).sort((a, b) => a - b)
      : spec.wireDiameter != null
        ? [Number(spec.wireDiameter)]
        : [];

    if (pDiams.length !== sDiams.length) continue;
    const diamsMatch = pDiams.every((d, i) => Math.abs(d - sDiams[i]) < 0.01);
    if (!diamsMatch) continue;

    if (pCounts.length && spec.numberOfWires) {
      const sCounts = Array.isArray(spec.numberOfWires)
        ? spec.numberOfWires.map(Number).sort((a, b) => a - b)
        : [Number(spec.numberOfWires)];
      if (pCounts.length !== sCounts.length) continue;
      const countsMatch = pCounts.every((c, i) => c === sCounts[i]);
      if (!countsMatch) continue;
    }

    return true;
  }

  return false;
}

// ── Single-select phantom card (original behavior for unwinding, conductor_isolation) ──
const SingleSelectCard = ({ phantom, onUsePhantom }) => {
  const wType = String(phantom.wireType || '').toUpperCase();
  const wDiam = Array.isArray(phantom.wireDiameter)
    ? phantom.wireDiameter.join(', ')
    : phantom.wireDiameter || '';
  const wCount = Array.isArray(phantom.numberOfWires)
    ? phantom.numberOfWires.join(', ')
    : phantom.numberOfWires || '';
  const showAvail = phantom.effectiveAvailable ?? phantom.availableLength;

  return (
    <div
      className="p-2 rounded border border-info bg-white"
      style={{ cursor: 'pointer', fontSize: 12 }}
      onClick={() => onUsePhantom && onUsePhantom({ ...phantom, availableLength: showAvail })}
    >
      <div className="d-flex justify-content-between align-items-center mb-1">
        <Badge bg="info" style={{ fontSize: 10 }}>PHANTOM</Badge>
        <span className="fw-bold">{fmt(showAvail)} m</span>
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {phantom.color && <Badge bg="dark" className="me-1">{phantom.color}</Badge>}
        {wType && <span className="me-1">{wType}</span>}
        {wDiam && <span className="me-1">Ø{wDiam}</span>}
        {wCount && <span className="me-1">×{wCount}</span>}
      </div>
      {phantom.drum_material && (
        <div className="text-muted" style={{ fontSize: 10 }}>
          {phantom.drum_material} {phantom.drum_diameter && `Ø${phantom.drum_diameter}`}
        </div>
      )}
      {phantom.note && (
        <div className="text-muted mt-1" style={{ fontSize: 10, fontStyle: 'italic' }}>
          {phantom.note}
        </div>
      )}
    </div>
  );
};

// ── Multi-select phantom card for pre_stranding (stock reels) ──
const MultiSelectPicker = ({ available, totalNeeded, onUsePhantoms, usedPhantomIds }) => {
  const [selections, setSelections] = useState({});

  const togglePhantom = (phantomId, maxLen) => {
    setSelections(prev => {
      if (prev[phantomId] !== undefined) {
        const next = { ...prev };
        delete next[phantomId];
        return next;
      }
      return { ...prev, [phantomId]: Math.min(maxLen, Math.max(0, totalNeeded - totalSelected(prev))) };
    });
  };

  const updateAmount = (phantomId, val) => {
    setSelections(prev => ({ ...prev, [phantomId]: Math.max(0, Number(val) || 0) }));
  };

  const totalSelected = (sel) =>
    Object.values(sel || selections).reduce((s, v) => s + (Number(v) || 0), 0);

  const selected = totalSelected();
  const canConfirm = selected > 0;

  const handleConfirm = () => {
    const result = Object.entries(selections)
      .filter(([, len]) => len > 0)
      .map(([phantomId, useLength]) => {
        const ph = available.find(p => p.phantomId === phantomId);
        return { ...ph, useLength };
      });
    if (result.length > 0) onUsePhantoms(result);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-700">
          Need: <b>{fmt(totalNeeded)} m</b> | Selected: <b className={selected >= totalNeeded ? 'text-success' : 'text-warning'}>{fmt(selected)} m</b>
          {selected < totalNeeded && <span className="text-muted ms-1">(rest will be produced)</span>}
        </small>
        {canConfirm && (
          <Button size="sm" variant="info" onClick={handleConfirm}>
            Use selected stock ({Object.keys(selections).filter(k => selections[k] > 0).length} reels)
          </Button>
        )}
      </div>
      <Row className="g-2">
        {available.map(ph => {
          const isSelected = selections[ph.phantomId] !== undefined;
          const wType = String(ph.wireType || '').toUpperCase();
          const wDiam = Array.isArray(ph.wireDiameter) ? ph.wireDiameter.join(', ') : ph.wireDiameter || '';

          return (
            <Col xs={12} sm={6} md={4} key={ph.phantomId}>
              <div
                className={`p-2 rounded border ${isSelected ? 'border-success bg-success-subtle' : 'border-info bg-white'}`}
                style={{ fontSize: 12 }}
              >
                <div
                  className="d-flex justify-content-between align-items-center mb-1"
                  style={{ cursor: 'pointer' }}
                  onClick={() => togglePhantom(ph.phantomId, ph.effectiveAvailable ?? ph.availableLength)}
                >
                  <Badge bg={isSelected ? 'success' : 'info'} style={{ fontSize: 10 }}>
                    {isSelected ? 'SELECTED' : 'STOCK'}
                  </Badge>
                  <span className="fw-bold">{fmt(ph.effectiveAvailable ?? ph.availableLength)} m available</span>
                </div>
                <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                  {wType && <span className="me-1">{wType}</span>}
                  {wDiam && <span className="me-1">Ø{wDiam}</span>}
                  sect. {ph.section}
                </div>
                {ph.note && (
                  <div className="text-muted mb-1" style={{ fontSize: 10, fontStyle: 'italic' }}>
                    {ph.note}
                  </div>
                )}
                {isSelected && (
                  <Form.Group className="mt-1">
                    <Form.Label className="mb-0" style={{ fontSize: 10 }}>Use (m):</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={ph.effectiveAvailable ?? ph.availableLength}
                      value={selections[ph.phantomId] || ''}
                      onChange={e => updateAmount(ph.phantomId, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ fontSize: 12 }}
                    />
                  </Form.Group>
                )}
              </div>
            </Col>
          );
        })}
      </Row>
    </>
  );
};

const PhantomTokenPicker = ({
  phaseName,
  section,
  wireSpecs,
  phaseData,
  totalNeeded,
  onUsePhantom,
  onUsePhantoms,
  usedPhantomIds = [],
  phantomAllocMap = {}
}) => {
  const params = useMemo(() => {
    const p = { phase: phaseName };
    if (section) p.section = section;

    // for pre_stranding, pass wire specs from phase data for precise matching
    if (phaseName === 'pre_stranding' && phaseData) {
      if (phaseData.type) p.wireType = phaseData.type;
      if (phaseData.wireDiameter) p.wireDiameter = JSON.stringify(phaseData.wireDiameter);
      if (phaseData.numberOfWires) p.numberOfWires = JSON.stringify(phaseData.numberOfWires);
    }

    return p;
  }, [phaseName, section, phaseData]);

  const { data: phantoms = [], isLoading } = useQuery({
    queryKey: ['phantoms', 'compatible', params],
    queryFn: () => fetchCompatible(params),
    enabled: !!phaseName,
    refetchInterval: 15000,
    staleTime: 10000
  });

  const available = useMemo(() => {
    return phantoms
      .map(p => {
        // adjust available length by already-allocated amounts in this wizard session
        const allocated = phantomAllocMap[p.phantomId] || 0;
        const effectiveAvailable = Math.max(0, p.availableLength - allocated);
        return { ...p, effectiveAvailable };
      })
      .filter(p => {
        // hide fully allocated phantoms
        if (p.effectiveAvailable <= 0) return false;
        if (phaseName === 'conductor_isolation' && wireSpecs?.length) {
          return isWireCompatible(p, wireSpecs);
        }
        return true;
      });
  }, [phantoms, phaseName, wireSpecs, phantomAllocMap]);

  if (isLoading) {
    return (
      <div className="text-muted fs-10 py-1">
        <Spinner animation="border" size="sm" className="me-1" />
        Checking stock...
      </div>
    );
  }

  if (!available.length) return null;

  const isMultiSelect = phaseName === 'pre_stranding';

  return (
    <Card className="mb-3 border-0 shadow-sm" style={{ borderLeft: '5px solid #0dcaf0' }}>
      <Card.Header className="bg-info-subtle fw-semibold py-2 d-flex justify-content-between align-items-center">
        <span>
          {isMultiSelect ? 'Available Stock Reels' : 'Available Phantom Tokens'}
          <Badge bg="info" className="ms-2">{available.length}</Badge>
        </span>
        <span className="text-muted fs-10">
          {isMultiSelect ? 'Select reels and quantities to use from stock' : 'Click to use as input'}
        </span>
      </Card.Header>
      <Card.Body className="pt-2 pb-2">
        {isMultiSelect ? (
          <MultiSelectPicker
            available={available}
            totalNeeded={totalNeeded || 0}
            onUsePhantoms={onUsePhantoms || (() => {})}
            usedPhantomIds={usedPhantomIds}
          />
        ) : (
          <Row className="g-2">
            {available.map(ph => (
              <Col xs={12} sm={6} md={4} key={ph.phantomId}>
                <SingleSelectCard phantom={ph} onUsePhantom={onUsePhantom} />
              </Col>
            ))}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export default PhantomTokenPicker;
